// Guest queue Inc 4a — the guest publish path. A promoted (paid grid slot) member
// publishes on camera. Clones StudioStage's publisher pattern with three deltas:
// (1) identity = BARE session.user.id, NOT makeIdentity — Inc-2 demote targets a
// participant by member id, so the guest MUST join under it; (2) no screenShare
// and no HostControls; (3) onLeave drops back to viewer mode instead of routing
// away. Role comes back 'guest' from the server (is_on_grid true).

import {
  ControlBar,
  LiveKitRoom,
  PreJoin,
  RoomAudioRenderer,
  StartAudio,
  type LocalUserChoices,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { useQuery } from '@tanstack/react-query';
import { VideoPresets } from 'livekit-client';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { BroadcastView } from './BroadcastView';
import { liveRoomService } from './liveRoomService';
import { LeaveOnUnload } from './StudioStage';
import { FeaturedSuperchatProvider } from './useFeaturedSuperchat';

import type { LiveRoomDetail } from './roomQueries';

import { useAuth } from '@/lib/auth/useAuth';

function GuestStatus({ label }: { label: string }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <p className="font-sans text-sm text-video-text-faint">{label}</p>
    </div>
  );
}

// The outbid countdown (Inc 4b). Shown over the guest's program when the caller
// is on-grid AND in their grace window. With no scheduler, the client itself
// pokes evaluate-grid when the timer hits zero — that fires the demote precisely
// in a quiet room. If the guest counter-bids enough, reconcile clears grace_until
// → the route's refetch drops yourGraceUntil → this overlay unmounts.
const STAY_PRESETS_CENTS = [500, 2000, 10000]; // $5 · $20 · $100

function OutbidOverlay({
  graceUntil,
  roomId,
  roomName,
}: {
  graceUntil: string;
  roomId: string;
  roomName: string;
}) {
  const [left, setLeft] = useState(() =>
    Math.max(0, Math.ceil((Date.parse(graceUntil) - Date.now()) / 1000)),
  );
  const poked = useRef(false);
  useEffect(() => {
    poked.current = false;
    const tick = () => {
      const s = Math.max(0, Math.ceil((Date.parse(graceUntil) - Date.now()) / 1000));
      setLeft(s);
      if (s <= 0 && !poked.current) {
        poked.current = true; // poke exactly once
        void liveRoomService
          .evaluateGrid(roomId)
          .catch((e) => console.error('evaluate-grid poke failed', e));
      }
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [graceUntil, roomId]);

  const stay = async (cents: number) => {
    try {
      const { url } = await liveRoomService.bidForGrid(roomName, cents);
      window.location.assign(url);
    } catch {
      toast.error('Could not start checkout. Please try again.');
    }
  };

  return (
    <div className="absolute inset-x-0 top-0 z-20 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-oxblood/90 px-4 py-2">
      <span className="font-sans text-sm font-medium text-video-text">
        Outbid — {left}s to stay on the grid
      </span>
      <span className="flex items-center gap-1.5">
        {STAY_PRESETS_CENTS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => void stay(c)}
            className="rounded-md bg-video-text px-2.5 py-1 font-sans text-xs font-medium text-video-bg hover:opacity-90"
          >
            ${c / 100}
          </button>
        ))}
      </span>
    </div>
  );
}

export function GuestStage({
  room,
  onLeave,
  graceUntil,
}: {
  room: LiveRoomDetail;
  onLeave: () => void;
  /** The caller's outbid grace deadline (Inc 4b), or null when safe. */
  graceUntil: string | null;
}) {
  const { session, profile } = useAuth();
  const [choices, setChoices] = useState<LocalUserChoices | null>(null);
  const [permError, setPermError] = useState<string | null>(null);

  // Mint AFTER PreJoin so the entered name is baked into the token. Identity is
  // the BARE user id (not makeIdentity) — the linchpin that lets Inc-2 demote
  // target this participant by member id.
  const tokenQuery = useQuery({
    queryKey: ['live-guest-token', room.livekitRoomName, session?.user.id, choices?.username],
    enabled: Boolean(choices && session),
    retry: false,
    staleTime: Infinity,
    queryFn: () =>
      liveRoomService.mintAccessToken(
        room.livekitRoomName,
        session!.user.id,
        'guest',
        choices!.username.trim() || undefined,
      ),
  });

  const role = tokenQuery.data?.role;

  // Demoted in the promotion→mint window → the server returns a non-guest role;
  // drop back to viewer rather than trying to publish without permission.
  useEffect(() => {
    if (role && role !== 'guest') onLeave();
  }, [role, onLeave]);

  if (permError) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
        <h2 className="font-serif text-xl text-video-text">Camera &amp; mic needed</h2>
        <p className="max-w-md font-sans text-sm text-video-text-muted">
          ArcheTelos needs camera and microphone access to put you on the grid. Allow access in your
          browser, then try again. ({permError})
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setPermError(null)}
            className="rounded-md bg-video-text px-5 py-2 font-sans text-sm font-medium text-video-bg"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={onLeave}
            className="rounded-md border border-video-line px-5 py-2 font-sans text-sm text-video-text-muted"
          >
            Back to watching
          </button>
        </div>
      </div>
    );
  }

  if (!choices) {
    return (
      <div className="mx-auto max-w-2xl p-6" data-lk-theme="default">
        <h1 className="mb-1 font-serif text-2xl text-video-text">
          You’re on the grid — {room.title}
        </h1>
        <p className="mb-4 font-sans text-sm text-video-text-muted">
          Choose your camera and mic, then go on camera. You can leave anytime and keep watching.
        </p>
        <PreJoin
          joinLabel="Go on camera"
          userLabel="Display name (shown on the stream)"
          defaults={{ username: profile?.display_name ?? profile?.username ?? '' }}
          onSubmit={(c) => setChoices(c)}
          onError={(e) => setPermError(e.message)}
        />
        <button
          type="button"
          onClick={onLeave}
          className="mt-4 font-sans text-xs text-video-text-faint hover:underline"
        >
          Never mind — keep watching
        </button>
      </div>
    );
  }

  if (tokenQuery.isLoading) return <GuestStatus label="Connecting…" />;
  if (tokenQuery.error) return <GuestStatus label="Couldn’t join the grid." />;
  if (!tokenQuery.data || role !== 'guest') return <GuestStatus label="Connecting…" />;

  return (
    <LiveKitRoom
      token={tokenQuery.data.token}
      serverUrl={tokenQuery.data.url}
      connect
      video={choices.videoEnabled}
      audio={choices.audioEnabled}
      options={{
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          deviceId: choices.videoDeviceId || undefined,
          resolution: VideoPresets.h720.resolution,
        },
        audioCaptureDefaults: { deviceId: choices.audioDeviceId || undefined },
        publishDefaults: { simulcast: true },
      }}
      data-lk-theme="default"
      className="flex h-full flex-col bg-video-bg"
      onDisconnected={onLeave}
    >
      <FeaturedSuperchatProvider>
        <div className="relative aspect-video w-full bg-black">
          <BroadcastView
            roomStatus="live"
            hostId={room.hostId}
            title={room.title}
            tickerLines={room.tickerLines}
            logoUrl={room.logoUrl}
          />
          <StartAudio
            label="Tap to unmute"
            className="absolute bottom-4 right-4 rounded-md bg-video-bg/70 px-3 py-1.5 font-sans text-xs text-video-text"
          />
          {graceUntil && (
            <OutbidOverlay
              graceUntil={graceUntil}
              roomId={room.id}
              roomName={room.livekitRoomName}
            />
          )}
        </div>
        {/* Guests get mic/camera/leave — never screen share. */}
        <ControlBar
          variation="minimal"
          controls={{ microphone: true, camera: true, screenShare: false, leave: true }}
        />
        <RoomAudioRenderer />
        <LeaveOnUnload roomName={room.livekitRoomName} />
      </FeaturedSuperchatProvider>
    </LiveKitRoom>
  );
}
